import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { agentsFiltersAtom } from "@/app/(saas)/(app)/business-brokers/_components/filters/state";
import { api } from "@/lib/api-client";
import type { DetailedAgent } from "@repo/database";

const LIMIT = 6;

/**
 * Walks up the subdivision hierarchy to find brokers in broader geographic
 * areas when the current location returns zero results.
 *
 * Cascade:
 *  1. If neighborhood → search city
 *  2. If city → search state (skip county for broader reach)
 *  3. If county → search state
 *  4. If state → search country-wide
 *  5. Fallback → top brokers country-wide by opportunity score
 */
export function useNearbyBrokers(opts: {
  enabled: boolean;
  countryCode?: string;
}): {
  brokers: DetailedAgent[];
  isNearby: boolean;
  parentLocationName: string | null;
  isLoading: boolean;
} {
  const filters = useAtomValue(agentsFiltersAtom);
  const location = filters.location;

  const hasNeighborhood = !!location?.context.neighborhood?.name;
  const hasCity = !!location?.context.place?.name;
  const hasCounty = !!location?.context.district?.name;
  const hasState = !!location?.context.region?.name;

  const broaderQuery = useMemo(() => {
    if (!opts.countryCode || !location) return null;

    const ctx = location.context;

    if (hasNeighborhood && hasCity) {
      return {
        countryCode: opts.countryCode,
        city: ctx.place?.name,
        county: ctx.district?.name,
        state: ctx.region?.name,
        sort: "recommended" as const,
      };
    }

    if (hasCity && hasState) {
      return {
        countryCode: opts.countryCode,
        state: ctx.region?.name,
        sort: "recommended" as const,
      };
    }

    if (hasCounty && hasState) {
      return {
        countryCode: opts.countryCode,
        state: ctx.region?.name,
        sort: "recommended" as const,
      };
    }

    if (hasState) {
      return {
        countryCode: opts.countryCode,
        sort: "recommended" as const,
      };
    }

    return null;
  }, [opts.countryCode, hasNeighborhood, hasCity, hasCounty, hasState, location]);

  const parentLocationName = useMemo(() => {
    if (!broaderQuery) return null;
    if (broaderQuery.state) return broaderQuery.state;
    if (broaderQuery.countryCode) {
      return location?.context.country?.name ?? broaderQuery.countryCode.toUpperCase();
    }
    return null;
  }, [broaderQuery, location]);

  const broaderResult = api.agents.get.useQuery(
    { ...(broaderQuery ?? {}), limit: LIMIT },
    {
      enabled: opts.enabled && !!broaderQuery,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const broaderEmpty =
    broaderResult.isSuccess && broaderResult.data.agents.length === 0;

  const featuredResult = api.agents.get.useQuery(
    {
      countryCode: opts.countryCode,
      sort: "recommended",
      limit: LIMIT,
    },
    {
      enabled: opts.enabled && (broaderEmpty || !broaderQuery),
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const brokersBroader = useMemo(
    () =>
      (broaderResult.data?.agents ?? [])
        .filter((a) => a.isVerified)
        .slice(0, LIMIT),
    [broaderResult.data],
  );

  const brokersFeatured = useMemo(
    () =>
      (featuredResult.data?.agents ?? [])
        .filter((a) => a.isVerified)
        .slice(0, LIMIT),
    [featuredResult.data],
  );

  const isNearby = brokersBroader.length > 0;
  const brokers = isNearby ? brokersBroader : brokersFeatured;

  const isLoading =
    (opts.enabled && !!broaderQuery && broaderResult.isLoading) ||
    (broaderEmpty && featuredResult.isLoading) ||
    (!broaderQuery && featuredResult.isLoading);

  return { brokers, isNearby, parentLocationName, isLoading };
}
